<script>
  import { createEventDispatcher } from 'svelte';

  const dispatch = createEventDispatcher();

  export let formSections = [];
  export let currentSectionIndex = 0;
  export let formData = {};
  export let formErrors = {};
  export let isEditing = false;
  export let questionnaireInfo = null;
  
  let isCurrentSectionValid = false;
  let contentContainer;
  let showQuestionnaireInfo = true; // Control whether to show the info page first

  // Reactive statement to track validation state
  $: {
    if (showQuestionnaireInfo) {
      isCurrentSectionValid = true; // Always valid on info page
    } else {
      const fields = renderSectionFields(formSections[currentSectionIndex]);
      isCurrentSectionValid = fields.every(
        (field) => !field.required || (formData[field.name] !== undefined && formData[field.name] !== null && formData[field.name] !== '')
      );
    }
  }

  // Calculate completion status for current section
  $: sectionCompletion = (() => {
    if (showQuestionnaireInfo) {
      return { total: 0, answered: 0, isComplete: true };
    }
    
    const fields = renderSectionFields(formSections[currentSectionIndex]);
    const requiredFields = fields.filter((field) => field.required);
    const answeredRequiredFields = requiredFields.filter(
      (field) => formData[field.name] !== undefined && formData[field.name] !== null && formData[field.name] !== ''
    );

    return {
      total: requiredFields.length,
      answered: answeredRequiredFields.length,
      isComplete: answeredRequiredFields.length === requiredFields.length,
    };
  })();

  function nextSection() {
    if (showQuestionnaireInfo) {
      // Move from info page to first section
      showQuestionnaireInfo = false;
      currentSectionIndex = 0;
      dispatch('sectionChange', { index: currentSectionIndex });
    } else if (currentSectionIndex < formSections.length - 1) {
      currentSectionIndex++;
      dispatch('sectionChange', { index: currentSectionIndex });
      // Scroll to top of content container
      if (contentContainer) {
        contentContainer.scrollTop = 0;
      }
    }
  }

  function previousSection() {
    if (currentSectionIndex > 0) {
      currentSectionIndex--;
      dispatch('sectionChange', { index: currentSectionIndex });
      // Scroll to top of content container
      if (contentContainer) {
        contentContainer.scrollTop = 0;
      }
    } else if (currentSectionIndex === 0 && !showQuestionnaireInfo) {
      // Go back to info page from first section
      showQuestionnaireInfo = true;
      currentSectionIndex = 0;
      dispatch('sectionChange', { index: -1 }); // -1 indicates info page
    }
  }

  function goToSection(index) {
    if (index >= 0 && index < formSections.length) {
      showQuestionnaireInfo = false;
      currentSectionIndex = index;
      dispatch('sectionChange', { index: currentSectionIndex });
      // Scroll to top of content container
      if (contentContainer) {
        contentContainer.scrollTop = 0;
      }
    }
  }

  function validateCurrentSection() {
    return isCurrentSectionValid;
  }

  function handleFormSubmit(e) {
    // Prevent any form submission from this component
    e.preventDefault();
    e.stopPropagation();
    return false;
  }

  function formatSectionHeader(header) {
    if (!header) return '';

    const formatted = header
      // Handle line breaks
      .replace(/\\n/g, '<br>')
      // Handle custom pseudo-HTML tags
      .replace(/<blue>/g, '<span class="text-blue-600 dark:text-blue-400">')
      .replace(/<\/blue>/g, '</span>')
      .replace(/<b>/g, '<strong>')
      .replace(/<\/b>/g, '</strong>')
      .replace(/<i>/g, '<em>')
      .replace(/<\/i>/g, '</em>');

    return formatted;
  }

  function formatQuestionnaireText(text) {
    if (!text) return '';

    const formatted = text
      // Handle line breaks
      .replace(/\\n/g, '<br>')
      // Handle custom pseudo-HTML tags
      .replace(/<blue>/g, '<span class="text-blue-600 dark:text-blue-400">')
      .replace(/<\/blue>/g, '</span>')
      .replace(/<b>/g, '<strong>')
      .replace(/<\/b>/g, '</strong>')
      .replace(/<i>/g, '<em>')
      .replace(/<\/i>/g, '</em>');

    return formatted;
  }

  function renderSectionFields(section) {
    const fields = [];

    if (!section || !section.sections) return fields;

    section.sections.forEach((subSection, index) => {
      if (subSection.type === 'component' && subSection.component) {
        const component = subSection.component;
        const field = {
          name: component.name,
          header: component.header,
          type: getFieldType(component.componentName),
          placeholder: component.placeholder,
          required: component.validations?.some((v) => v.required) || false,
          options: component.configuration?.options || [],
          rank: component.rank || null,
          index: index + 1,
        };
        fields.push(field);
      }
    });

    return fields;
  }

  function getFieldType(componentName) {
    switch (componentName) {
      case 'InputTextComponent':
        return 'text';
      case 'InputNumberComponent':
        return 'number';
      case 'InputDateComponent':
        return 'date';
      case 'InputTextAreaComponent':
        return 'textarea';
      case 'InputDropdownComponent':
        return 'select';
      case 'DTSQQuestionComponent':
        return 'dtsq';
      default:
        return 'text';
    }
  }
</script>

<div class="space-y-1" on:submit={handleFormSubmit}>
  <!-- Questionnaire Info Page -->
  {#if showQuestionnaireInfo && questionnaireInfo}
    <div class="space-y-6">
      <!-- Header -->
      <div class="text-center">
        <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Questionnaire Information
        </h2>
        <p class="text-gray-600 dark:text-gray-400">
          Please review the questionnaire details before proceeding
        </p>
      </div>

      <!-- Questionnaire Name -->
      {#if questionnaireInfo.name}
        <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Questionnaire Name
          </h3>
          <div class="prose prose-sm max-w-none text-gray-700 dark:text-gray-300">
            {@html formatQuestionnaireText(questionnaireInfo.name)}
          </div>
        </div>
      {/if}

      <!-- Questionnaire Description -->
      {#if questionnaireInfo.description}
        <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Description
          </h3>
          <div class="prose prose-sm max-w-none text-gray-700 dark:text-gray-300 whitespace-pre-line">
            {@html formatQuestionnaireText(questionnaireInfo.description)}
          </div>
        </div>
      {/if}

      <!-- Copyright Information -->
      {#if questionnaireInfo.copyright}
        <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Copyright
          </h3>
          <div class="prose prose-sm max-w-none text-gray-700 dark:text-gray-300">
            {@html formatQuestionnaireText(questionnaireInfo.copyright)}
          </div>
        </div>
      {/if}

      <!-- Navigation buttons for info page -->
      <div class="pt-4 border-t border-gray-200 dark:border-gray-600">
        <div class="flex justify-between">
          <button
            type="button"
            class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-700 transition-colors"
            on:click={() => dispatch('cancel')}
          >
            Cancel
          </button>

          <button
            type="button"
            class="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors"
            on:click={nextSection}
          >
            Continue to Questions
          </button>
        </div>
      </div>
    </div>
  {:else}
    <!-- Section navigation header - only show if multiple sections -->
    {#if formSections.length > 1}
      <!-- Section info and progress indicator on one line -->
      <div class="flex items-center justify-between mb-4">
        <div class="text-sm text-gray-600 dark:text-gray-400">
          Section {currentSectionIndex + 1} of {formSections.length}
        </div>

        <!-- Progress dots -->
        <div class="flex space-x-1">
          {#each formSections as section, index}
            <div
              class="w-2 h-2 rounded-full transition-colors"
              class:bg-blue-600={index === currentSectionIndex}
              class:bg-gray-300={index < currentSectionIndex}
              class:bg-gray-200={index > currentSectionIndex}
              class:dark:bg-gray-600={index < currentSectionIndex}
              class:dark:bg-gray-500={index > currentSectionIndex}
            ></div>
          {/each}
        </div>
      </div>

      <!-- Header and sectionHeader as one unit -->
      <div class="mb-0">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {@html formatSectionHeader(formSections[currentSectionIndex].header)}
        </h3>

        {#if formSections[currentSectionIndex].sectionHeader}
          <div class="mb-2 text-sm text-gray-700 dark:text-gray-300 prose prose-sm max-w-none">
            {@html formatSectionHeader(formSections[currentSectionIndex].sectionHeader)}
          </div>
        {/if}
      </div>
    {:else}
      <!-- Single section header without navigation -->
      <div class="mb-0">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {@html formatSectionHeader(formSections[currentSectionIndex].header)}
        </h3>

        {#if formSections[currentSectionIndex].sectionHeader}
          <div class="mb-2 text-sm text-gray-700 dark:text-gray-300 prose prose-sm max-w-none">
            {@html formatSectionHeader(formSections[currentSectionIndex].sectionHeader)}
          </div>
        {/if}
      </div>
    {/if}

    <!-- Current section content with scrollable area -->
    <div
      bind:this={contentContainer}
      class="space-y-0 max-h-[32rem] overflow-y-auto pr-0 pt-4"
      style="scrollbar-width: thin;"
    >
      {#each renderSectionFields(formSections[currentSectionIndex]) as field}
        <div class="form-field">
          {#if field.type !== 'dtsq'}
            <label
              for={field.name}
              class="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {field.header}
              {#if field.required}
                <span class="text-red-500">*</span>
              {/if}
            </label>
          {/if}

          {#if field.type === 'text'}
            <input
              type="text"
              id={field.name}
              bind:value={formData[field.name]}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
              class:border-red-500={formErrors[field.name]}
              placeholder={field.placeholder || ''}
            />
          {:else if field.type === 'number'}
            <input
              type="number"
              id={field.name}
              bind:value={formData[field.name]}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
              class:border-red-500={formErrors[field.name]}
              placeholder={field.placeholder || ''}
            />
          {:else if field.type === 'date'}
            <input
              type="datetime-local"
              id={field.name}
              bind:value={formData[field.name]}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
              class:border-red-500={formErrors[field.name]}
            />
          {:else if field.type === 'textarea'}
            <textarea
              id={field.name}
              bind:value={formData[field.name]}
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
              class:border-red-500={formErrors[field.name]}
              placeholder={field.placeholder || ''}
            ></textarea>
          {:else if field.type === 'select'}
            <select
              id={field.name}
              bind:value={formData[field.name]}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
              class:border-red-500={formErrors[field.name]}
            >
              <option value="">Select an option</option>
              {#each field.options || [] as option}
                <option value={option.code}>{option.label}</option>
              {/each}
            </select>
          {:else if field.type === 'dtsq'}
            <div class="dtsq-question-compact">
              <div class="space-y-2">
                <!-- Row 1: Question text with numbering -->
                <div class="flex-1 min-w-0">
                  <!-- svelte-ignore a11y-label-has-associated-control -->
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <!-- {#if field.index !== undefined}
                      <span class="inline-block w-6 font-black text-gray-700 dark:text-gray-300"
                        >{field.index}.</span
                      >
                    {/if} -->
                    {field.header}
                    {#if field.required}
                      <span class="text-red-500">*</span>
                    {/if}
                  </label>
                </div>

                <!-- Row 2: Rank descriptions left and right -->
                <div
                  class="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400"
                >
                  <span class="whitespace-nowrap">{field.rank?.low || 'Never'}</span>
                  <span class="whitespace-nowrap">{field.rank?.high || 'Almost Always'}</span>
                </div>

                <!-- Row 3: Buttons spanning full width with dynamic sizing -->
                <div class="flex gap-1 w-full">
                  {#each field.options || [] as option}
                    <button
                      type="button"
                      class="h-auto min-h-8 border rounded text-xs font-medium transition-colors flex items-center justify-center px-1 py-2 flex-1 text-center leading-tight"
                      class:selected={formData[field.name] === option.code}
                      class:border-blue-500={formData[field.name] === option.code}
                      class:bg-blue-50={formData[field.name] === option.code}
                      class:dark:bg-blue-900={formData[field.name] === option.code}
                      class:dark:text-blue-100={formData[field.name] === option.code}
                      class:border-gray-300={formData[field.name] !== option.code}
                      class:dark:border-gray-600={formData[field.name] !== option.code}
                      class:bg-white={formData[field.name] !== option.code}
                      class:dark:bg-gray-700={formData[field.name] !== option.code}
                      class:text-gray-700={formData[field.name] !== option.code}
                      class:dark:text-gray-300={formData[field.name] !== option.code}
                      class:hover:bg-gray-50={formData[field.name] !== option.code}
                      class:dark:hover:bg-gray-600={formData[field.name] !== option.code}
                      on:click={() => (formData[field.name] = option.code)}
                    >
                      {option.label}
                    </button>
                  {/each}
                </div>
              </div>
            </div>
          {/if}
        </div>
      {/each}
    </div>

    <!-- Section completion status and guidance -->
    <div class="flex items-center justify-end mb-1 text-xs text-gray-600 dark:text-gray-400">
      {#if sectionCompletion.total > 0}
        {#if sectionCompletion.isComplete}
          <!-- All required questions answered -->
          <div class="my-2 flex items-center text-green-600 dark:text-green-400">
            <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"
              ></path>
            </svg>
            {#if currentSectionIndex === formSections.length - 1}
              Click 'Submit' to complete
            {:else}
              Click 'Next' to continue
            {/if}
          </div>
        {:else}
          <!-- Some required questions unanswered -->
          <div class="my-2 flex items-center text-gray-500 dark:text-gray-400">
            <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              ></path>
            </svg>
            {sectionCompletion.answered} of {sectionCompletion.total} required questions answered
            {#if sectionCompletion.total > sectionCompletion.answered}
              <span class="ml-1">• Scroll for more questions</span>
            {/if}
          </div>
        {/if}
      {:else}
        <!-- No required questions in this section -->
        <div class="my-2 text-gray-500 dark:text-gray-400">
          {#if currentSectionIndex === formSections.length - 1}
            Click 'Submit' to complete
          {:else}
            Click 'Next' to continue
          {/if}
        </div>
      {/if}
    </div>

    <!-- Section navigation buttons -->
    <div class="pt-4 border-t border-gray-200 dark:border-gray-600">
      <div class="flex justify-between">
        <button
          type="button"
          class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-700 transition-colors"
          class:invisible={currentSectionIndex === 0 && !questionnaireInfo}
          on:click={previousSection}
        >
          Previous
        </button>

        <div class="flex space-x-2">
          <button
            type="button"
            class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-700 transition-colors"
            on:click={() => dispatch('cancel')}
          >
            Cancel
          </button>

          {#if currentSectionIndex === formSections.length - 1}
            <button
              type="button"
              class="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors"
              class:opacity-50={!isCurrentSectionValid}
              class:cursor-not-allowed={!isCurrentSectionValid}
              class:hover:bg-blue-700={isCurrentSectionValid}
              class:hover:bg-blue-600={!isCurrentSectionValid}
              on:click={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (isCurrentSectionValid) {
                  dispatch('submit');
                }
              }}
            >
              {isEditing ? 'Update' : 'Submit'}
            </button>
          {:else}
            <button
              type="button"
              class="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors"
              class:opacity-50={!isCurrentSectionValid}
              class:cursor-not-allowed={!isCurrentSectionValid}
              class:hover:bg-blue-700={isCurrentSectionValid}
              class:hover:bg-blue-600={!isCurrentSectionValid}
              on:click={() => {
                if (isCurrentSectionValid) {
                  nextSection();
                }
              }}
            >
              Next
            </button>
          {/if}
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .dtsq-question-compact {
    margin-bottom: 2rem;
  }

  .dtsq-question-compact .flex {
    align-items: center;
  }

  .dtsq-question-compact button {
    transition: all 0.2s ease-in-out;
    min-width: 2.5rem;
  }

  .dtsq-question-compact button:hover {
    transform: translateY(-1px);
  }

  .dtsq-question-compact button.selected {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  /* Custom scrollbar styling */
  .overflow-y-auto::-webkit-scrollbar {
    width: 6px;
  }

  .overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
</style>
